import React from 'react'
import { TouchableOpacity, View } from 'react-native'
import { useTheme } from '@ui-kitten/components'
import { StyleService, useStyleSheet } from '@src/style/service'
import { Icon, IconName, Text } from '@components/base'
import { useIsDarkMode } from '@src/config/theme'
import Snackbar from './Snackbar'

export type SnackLevel = 'success' | 'warning' | 'error'

export interface SnackContentRedesignedProps {
  title: string
  iconName?: IconName
  action?: { label: string; onPress: () => void }
  level: 'success' | 'warning' | 'error'
  isVisible: boolean
  onDismiss: () => void
}

export const SnackContentRedesigned = (props: SnackContentRedesignedProps) => {
  const { title, iconName, action, level, isVisible, onDismiss } = props
  const styles = useStyleSheet(themedStyle)
  const isDarkMode = useIsDarkMode()
  const theme = useTheme()

  const snackStyle = () => {
    switch (level) {
      case 'success':
        return styles.successLevel
      case 'warning':
        return styles.warningLevel
      case 'error':
        return styles.errorLevel
      default:
        return
    }
  }

  const iconStyle = () => {
    switch (level) {
      case 'success':
        return styles.successIcon
      case 'warning':
        return styles.warningIcon
      case 'error':
        return styles.errorIcon
      default:
        return
    }
  }

  const handleActionPress = () => {
    if (!action) {
      return
    }

    action.onPress()
    onDismiss()
  }

  return (
    <Snackbar
      style={[styles.snackBar, snackStyle()]}
      contentStyle={styles.snackContent}
      onDismiss={onDismiss}
      visible={isVisible}
      duration={Snackbar.DURATION_MEDIUM}
      testID="Snackbar"
    >
      <View style={styles.container}>
        {iconName && <Icon name={iconName} style={[styles.icon, iconStyle()]} />}

        <Text
          type="regular"
          style={{
            flex: 1,
            color: isDarkMode ? theme['theme.tooltip.textPrimary'] : theme['theme.text.primary'],
          }}
          testID="Snackbar/TitleText"
        >
          {title}
        </Text>

        {action && (
          <TouchableOpacity accessibilityLabel={action.label} onPress={handleActionPress}>
            <Text type="regular" bold style={styles.action}>
              {action.label}
            </Text>
          </TouchableOpacity>
        )}
      </View>
    </Snackbar>
  )
}

const themedStyle = StyleService.create({
  snackBar: {
    backgroundColor: 'theme.background',
    borderRadius: 8,
    borderWidth: 1,
  },
  snackContent: {
    margin: 0,
  },
  container: {
    flexDirection: 'row',
    width: '100%',
    gap: 10,
  },
  successLevel: {
    backgroundColor: 'theme.success.lightest',
    borderColor: 'theme.success.lighter',
  },
  warningLevel: {
    backgroundColor: 'theme.warning.lightest',
    borderColor: 'theme.warning.lighter',
  },
  errorLevel: {
    backgroundColor: 'theme.error.lightest',
    borderColor: 'theme.error.lighter',
  },
  successIcon: {
    color: 'theme.success.darkest',
  },
  warningIcon: {
    color: 'theme.warning.darkest',
  },
  errorIcon: {
    color: 'theme.error.darkest',
  },
  action: {
    color: 'theme.text.link',
    alignSelf: 'flex-end',
    marginTop: 8,
  },
  icon: {
    height: 18,
    width: 18,
  },
})
